import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { BaseModalProvider } from './BaseModalContext';
import styles from './BaseModal.module.scss';
import BaseModalHeader from './BaseModalHeader';
import BaseModalContent from './BaseModalContent';

interface BaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
}

export const BaseModal: React.FC<BaseModalProps> & {
    Header: typeof BaseModalHeader;
    Content: typeof BaseModalContent;
} = ({ isOpen, onClose, children, className }) => {
    // Handle ESC key press
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Check if there's any dropdown open in the DOM
        const dropdownExists = document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
        
        if (dropdownExists) {
            return;
        }
        
        // Only close if clicking on the overlay itself (not on children)
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

           const modalContent = (
               <div 
                   className={styles.modalOverlay} 
                   onClick={handleOverlayClick}
               >
            <div 
                className={`${styles.modalContainer} ${className || ''}`}
            >
                <BaseModalProvider onClose={onClose}>
                    {children}
                </BaseModalProvider>
            </div>
        </div>
    );

    // Render modal in portal to avoid z-index issues
    return createPortal(modalContent, document.body);
};

// Attach sub-components to create composition API
BaseModal.Header = BaseModalHeader;
BaseModal.Content = BaseModalContent;

export default BaseModal;
